import { useMemo, useRef } from 'react'
import { getKey, keys } from '@/features/shared/constants/keyCodes'

type OnChangeType<T> = (
  option: T,
  { explicitSelection }: { explicitSelection: boolean },
  passthroughProps?: Record<string, unknown>,
) => void

type Props<T, K> = {
  onChange?: OnChangeType<T>
  optionValues: T[]
  value: K
  getValue: (val: T) => K
}

export const useRadio = <T, K, R extends HTMLElement = HTMLDivElement>({
  onChange,
  optionValues = [],
  value,
  getValue,
}: Props<T, K>) => {
  const selectedIndex = useMemo(
    () => optionValues.findIndex((oValue) => getValue(oValue) === value),
    [optionValues, value, getValue],
  )
  const focusedIndex = selectedIndex > -1 ? selectedIndex : 0
  const radioRefs = useRef<Record<string, R>>({})

  const handleSelect = (index: number, explicitSelection: boolean, passthroughProps = {}) => {
    const radioEl = radioRefs.current[`radio-${index}`]
    radioEl?.focus()
    if (optionValues[index]) {
      onChange?.(optionValues[index], { explicitSelection }, passthroughProps)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, passthroughProps = {}) => {
    switch (getKey(e)) {
      case keys.SPACE: {
        e.preventDefault()
        handleSelect(focusedIndex, true, passthroughProps)
        break
      }
      case keys.ARROW_RIGHT:
      case keys.ARROW_DOWN: {
        e.preventDefault()
        const newIndex = (focusedIndex + 1) % optionValues.length
        handleSelect(newIndex, false, passthroughProps)
        break
      }
      case keys.ARROW_LEFT:
      case keys.ARROW_UP: {
        e.preventDefault()
        const newValue = focusedIndex > 0 ? focusedIndex - 1 : optionValues.length - 1
        handleSelect(newValue, false, passthroughProps)
        break
      }
      default:
        break
    }
  }

  const getRadioGroupProps = () => ({
    role: 'radiogroup',
  })

  const getRadioBtnProps = (optionIndex: number, passthroughProps = {}) => ({
    role: 'radio',
    tabIndex: optionIndex === focusedIndex ? 0 : -1,
    'aria-checked': optionIndex === selectedIndex,
    ref: (ref: R) => {
      radioRefs.current[`radio-${optionIndex}`] = ref
    },
    onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, passthroughProps),
    onClick: () => handleSelect(optionIndex, true, passthroughProps),
  })

  return {
    getRadioGroupProps,
    getRadioBtnProps,
    selectedIndex,
  }
}
